import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InputUnit } from '../../../types';
import { INPUT_UNITS } from '../../../types';
import { Check, X, Icon } from '../../ui/Icon';
import { parseAmount } from './pantryUnits';

interface Props {
  amount: string;
  unit: InputUnit;
  /** Resolves false to stay open (a unit clash, a failed save). */
  onSave: (amount: number, unit: InputUnit) => Promise<boolean>;
  onCancel: () => void;
}

/**
 * The same amount + unit pair every add row uses, opened in place of a chip to
 * correct it. Enter saves, Escape leaves it as it was.
 */
export default function QuantityEditor({ amount: initialAmount, unit: initialUnit, onSave, onCancel }: Props) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(initialAmount);
  const [unit, setUnit] = useState<InputUnit>(initialUnit);

  async function save() {
    const value = parseAmount(amount);
    if (value !== null) await onSave(value, unit);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') onCancel();
  }

  return (
    <span className="inline-flex items-center rounded-md border border-teal-400 dark:border-teal-600 bg-white dark:bg-slate-800 align-middle">
      <input
        autoFocus
        value={amount}
        onChange={e => setAmount(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={e => e.target.select()}
        inputMode="decimal"
        aria-label={t('meals.ingredients.amountLabel')}
        className="w-12 bg-transparent px-1.5 py-0.5 text-[11px] font-mono tabular-nums text-slate-800 dark:text-slate-100 focus:outline-none"
      />
      <select
        value={unit}
        onChange={e => setUnit(e.target.value as InputUnit)}
        onKeyDown={onKeyDown}
        aria-label={t('meals.ingredients.unit')}
        className="bg-transparent border-l border-slate-100 dark:border-slate-700 px-1 py-0.5 text-[11px] text-slate-600 dark:text-slate-300 focus:outline-none cursor-pointer"
      >
        {INPUT_UNITS.map(u => <option key={u} value={u}>{t(`meals.units.${u}`)}</option>)}
      </select>
      <button
        onClick={save}
        className="w-5 h-5 flex items-center justify-center text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 cursor-pointer"
        aria-label={t('common.save')}
      >
        <Icon as={Check} size={11} />
      </button>
      <button
        onClick={onCancel}
        className="w-5 h-5 flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
        aria-label={t('common.cancel')}
      >
        <Icon as={X} size={11} />
      </button>
    </span>
  );
}
